import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment-timezone';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { ParameterTypeEnum } from 'src/app/shared/enums/parameter-type.enum';
import { S3UrlService } from 'src/app/shared/services/s3-url.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import {
  BinanceWallet,
  DashboardResponse,
  DepositNetwork,
  DepositRecord,
  KycStatus,
  MasterSnapshot,
  PrevalidacionStatusResponse,
  SyncStatus,
  UserWithWallet,
  UsersWithWalletFilters,
  WalletFilter,
  WalletStatus,
} from '../../interfaces/binance-wallet.interface';
import { BinanceCentralizationService } from '../../services/binance-centralization.service';
import { BinanceWalletAdminService } from '../../services/binance-wallet-admin.service';
import { ParameterService } from '../../services/parameter.service';
import {
  CreateWalletModalComponent,
  CreateWalletModalData,
  CreateWalletModalResult,
} from './create-wallet-modal/create-wallet-modal.component';

@Component({
  selector: 'app-wallets-usuarios',
  templateUrl: './wallets-usuarios.component.html',
  styleUrls: ['./wallets-usuarios.component.sass'],
})
export class WalletsUsuariosComponent implements OnInit, OnDestroy {
  /**
   * Listado principal: usuarios de Aguacate con su wallet Binance Link
   * (o sin ella). `walletId === null` indica que el usuario todavia no
   * tiene wallet activa.
   */
  users: UserWithWallet[] = [];

  /**
   * Estado del dashboard: master info + agregados locales. `null` mientras
   * no termino la primera carga.
   */
  dashboard: DashboardResponse | null = null;
  isSyncing = false;

  /**
   * Timeline de snapshots del master para el sparkline. Se carga junto al
   * dashboard y se refresca tras cada sync manual. Orden: mas reciente
   * primero (asi viene del backend); el chart invierte para pintar
   * left-to-right.
   */
  historySnapshots: MasterSnapshot[] = [];
  /** Capacidad maxima del buffer del backend (para mostrar "N/MAX"). */
  historyCapacity = 0;

  /**
   * Estado de la sincronización masiva de prevalidación KYC.
   * Carga al montar el componente y se polea cada 5s mientras hay un
   * job en RUNNING. Render en card aparte arriba del listado.
   */
  prevalidacion: PrevalidacionStatusResponse | null = null;
  /**
   * Mensaje de error de la última carga del status. Si está poblado, la
   * card muestra un banner rojo con este texto en lugar de los counters.
   * Útil para debugging cuando el endpoint backend falla (ej. tabla no
   * existe, deploy desactualizado).
   */
  prevalidacionLoadError: string | null = null;
  /** `true` mientras se está haciendo la primera carga del status. */
  prevalidacionLoading = true;
  /** Handler del setInterval del polling — limpiar en ngOnDestroy. */
  private prevalidacionPoll: ReturnType<typeof setInterval> | null = null;

  displayedColumns: string[] = [
    'user',
    'email',
    'pais',
    // Saldo USDT del cardex interno: visible siempre, exista o no la
    // sub-cuenta Binance Link. Va antes que walletStatus para que el
    // operador lo vea primero al barrer la tabla.
    'usdtBalance',
    'walletStatus',
    'kycStatus',
    'lastBalanceBtc',
    // Saldo USDT live de la sub-cuenta Binance (no centralizado aún).
    // Aparece pegado a BTC porque comparten el mismo timestamp de sync.
    'lastBalanceUsdt',
    'lastDepositAt',
    'acciones',
  ];
  // Nota: el badge de prevalidación KYC interna NO es columna propia —
  // va inline dentro de la celda `user`, debajo del nombre, sólo para
  // usuarios sin wallet (la decisión que va a tomar el operador antes
  // de crear la sub). Mantiene el ancho de la tabla y agrupa toda la
  // info de identidad del usuario en una sola columna.

  /**
   * `usuarioId` de la fila cuyo botón "Prevalidar" está en vuelo. Sirve
   * para deshabilitar el botón y mostrar spinner sólo en esa fila sin
   * bloquear el resto. `null` cuando no hay ningún revalidate en curso.
   */
  revalidatingUserId: number | null = null;

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalItems = 0;
  totalPages = 0;

  // Filtros (simplificados respecto a la version anterior)
  filterQ: string = '';
  filterHasWallet: WalletFilter = 'all';

  hasWalletOptions: { value: WalletFilter; label: string }[] = [
    { value: 'all', label: 'Todos los usuarios' },
    { value: 'with', label: 'Con wallet' },
    { value: 'without', label: 'Sin wallet' },
  ];

  expandedCardId: number | null = null;

  /**
   * Hash del email del operador (SHA-256). Se compara contra el parámetro
   * `CORREOS_AUT` para gating de acciones sensibles (delete-binance-sub).
   * Mismo patrón que `users-balance.component`.
   */
  private operatorHash: string = '';
  /**
   * `true` si el hash del operador está incluido en la lista de
   * autorizados. Habilita el botón "Eliminar sub-cuenta Binance".
   */
  canDeleteBinanceSub: boolean = false;

  constructor(
    private walletAdminService: BinanceWalletAdminService,
    private spinner: SpinnerService,
    private dialog: MatDialog,
    private auth: CognitoService,
    private parameterService: ParameterService,
    private centralizationService: BinanceCentralizationService,
    private s3UrlService: S3UrlService,
  ) {}

  /**
   * Visibilidad del botón "Centralizar pendientes". Requisitos:
   *  - `subAccountId` real (NO empieza con `pending-` — esos son placeholders
   *    de wallets que nunca llegaron a crearse en Binance).
   *  - `walletStatus !== 'CREATING'` (sub aún está creándose).
   *  - Wallet existente (`walletId !== null`).
   * El gating por rol `aguacate_operator` lo hace el OperatorGuard.
   */
  canRunManualCentralization(user: UserWithWallet): boolean {
    if (!user.walletId) return false;
    if (!user.subAccountId) return false;
    if (user.subAccountId.startsWith('pending-')) return false;
    if (user.walletStatus === 'CREATING') return false;
    return true;
  }

  /**
   * Confirma y ejecuta la centralización manual de depósitos pendientes
   * de la sub-cuenta del usuario. Caso típico: wallets en
   * `kycStatus = REJECT` que el cron automático no procesa.
   *
   * Idempotente — depósitos ya centralizados antes no se duplican.
   */
  async openManualCentralizationModal(user: UserWithWallet): Promise<void> {
    if (!this.canRunManualCentralization(user) || !user.walletId) return;

    const kycBadge =
      user.kycStatus === 'PASS'
        ? `<span style="background: #dcfce7; color: #15803d;">${user.kycStatus}</span>`
        : `<span style="background: #fee2e2; color: #b91c1c;">${user.kycStatus} ⚠</span>`;

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Centralizar depósitos pendientes',
      html: `
        <div style="text-align: left; font-size: 0.85rem; color: #374151;">
          <p style="margin: 0 0 12px 0;">
            Esta acción mueve los USDT que estén en la sub-cuenta de Binance hacia la
            cuenta master, y acredita el monto al saldo del usuario en su cardex
            interno (<code>wallet_cardex_user</code>).
          </p>
          <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; font-size: 0.82rem;">
            <p style="margin: 0 0 4px 0;"><strong>Usuario:</strong> ${user.usuarioId} — ${user.email || '—'}</p>
            <p style="margin: 0 0 4px 0;"><strong>subAccountId:</strong> <code>${user.subAccountId}</code></p>
            <p style="margin: 0 0 4px 0;"><strong>kycStatus:</strong>
              <span style="display: inline-block; padding: 2px 8px; border-radius: 999px; font-weight: 700; font-size: 0.72rem;">${kycBadge}</span>
            </p>
            <p style="margin: 0;"><strong>walletStatus:</strong>
              <span style="display: inline-block; padding: 2px 8px; border-radius: 999px; background: #e5e7eb; color: #374151; font-weight: 700; font-size: 0.72rem;">${user.walletStatus}</span>
            </p>
          </div>
          <p style="margin: 12px 0 0 0; font-size: 0.78rem; color: #6b7280; font-style: italic;">
            Idempotente: depósitos ya centralizados antes NO se duplican. La
            operación puede tardar hasta 30s por depósito.
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Centralizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      width: '560px',
    });

    if (!result.isConfirmed) return;

    // Spinner global durante la operación. La promesa puede tardar
    // varios minutos para sub-cuentas con muchos depósitos.
    const ref = this.spinner.start();
    try {
      const response = await this.centralizationService.runManualCentralization(
        user.walletId,
      );
      this.spinner.stop(ref);
      const yaCentralizados =
        response.depositsScanned - response.depositsCentralized;

      await Swal.fire({
        icon: 'success',
        title: 'Centralización completada',
        html: `
          <div style="text-align: left; font-size: 0.85rem; color: #374151;">
            <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 12px;">
              <p style="margin: 0 0 6px 0; display: flex; justify-content: space-between;">
                <span style="color: #6b7280;">Depósitos escaneados</span>
                <strong style="color: #1e5a3f;">${response.depositsScanned}</strong>
              </p>
              <p style="margin: 0 0 6px 0; display: flex; justify-content: space-between;">
                <span style="color: #6b7280;">Centralizados ahora</span>
                <strong style="color: #16a34a;">${response.depositsCentralized}</strong>
              </p>
              <p style="margin: 0; display: flex; justify-content: space-between;">
                <span style="color: #6b7280;">Ya centralizados antes</span>
                <strong style="color: #6b7280;">${yaCentralizados}</strong>
              </p>
            </div>
            <p style="margin: 12px 0 0 0; font-size: 0.78rem; color: #6b7280;">
              Sub-cuenta: <code>${response.subAccountId}</code>
            </p>
          </div>
        `,
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1e5a3f',
      });

      // Refresca la fila (puede haber cambiado lastDepositAt/totalDepositsCount)
      // y el dashboard de centralizaciones lo refleja al entrar.
      await this.loadUsers();
    } catch (error: any) {
      this.spinner.stop(ref);
      const status = error?.status;
      const apiMsg = error?.error?.message;
      let title = 'Error al centralizar';
      let text = apiMsg || 'No se pudo ejecutar la centralización manual.';

      if (status === 404) {
        title = 'Wallet no encontrada';
        text = 'La wallet no existe en BD.';
      } else if (status === 409) {
        title = 'Sub-cuenta sin crear';
        text =
          'La wallet nunca llegó a crearse en Binance. No hay depósitos para centralizar.';
      } else if (status === 500) {
        title = 'Backend mal configurado';
        text =
          'Falta configuración del broker master. Escalar a equipo backend.';
      } else if (status === 502) {
        title = 'Error de red contra Binance';
        text = apiMsg || 'Error temporal con Binance. Podés reintentar.';
      }

      Swal.fire({
        icon: 'error',
        title,
        text,
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  async ngOnInit(): Promise<void> {
    // Dashboard (KPIs + master) + history + listado + estado prevalidación
    // + check de autorización para acciones sensibles en paralelo.
    await Promise.all([
      this.loadDashboard(),
      this.loadHistory(),
      this.loadUsers(),
      this.loadPrevalidacion(),
      this.checkDeleteSubAuthorization(),
    ]);
    // Si al montar ya hay un job RUNNING, arranco el polling.
    if (this.prevalidacion?.isRunning) {
      this.startPrevalidacionPolling();
    }
  }

  /**
   * Evalúa si el operador actual puede ver el botón
   * "Eliminar sub-cuenta Binance". Compara `SHA-256(email)` contra el
   * parámetro `CORREOS_AUT` (lista de hashes autorizados).
   * Mismo patrón que `users-balance.component.initFormCuenta`.
   */
  private async checkDeleteSubAuthorization(): Promise<void> {
    try {
      const cognitoUser = await this.auth.getUser();
      const email = (cognitoUser?.attributes?.['email'] ?? '')
        .toUpperCase()
        .toString();
      if (!email) {
        this.canDeleteBinanceSub = false;
        return;
      }
      this.operatorHash = await this.generateCod(email);
      const validEmails = await this.parameterService.getParameter(
        ParameterTypeEnum.CORREOS_AUTORIZADOS,
      );
      const list: string = validEmails?.valor ?? '';
      this.canDeleteBinanceSub = list.includes(this.operatorHash);
    } catch {
      // Defensivo: ante cualquier error, ocultamos el botón.
      this.canDeleteBinanceSub = false;
    }
  }

  private async generateCod(input: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Modal manual para eliminar una sub-cuenta Binance. El operador tipea
   * el `subAccountId` y confirma — esto evita borrados accidentales por
   * row click. Llama `POST /binance-link/wallets/:subAccountId/delete-binance-sub`.
   */
  async openDeleteBinanceSubModal(): Promise<void> {
    if (!this.canDeleteBinanceSub) return;

    const { value: subAccountId } = await Swal.fire({
      icon: 'warning',
      title: 'Eliminar sub-cuenta Binance',
      html: `
        <p style="text-align: left; margin: 0 0 12px 0; font-size: 0.85rem; color: #374151;">
          Esta acción solicita al backend la <strong>baja definitiva</strong> de una sub-cuenta
          en Binance. Sólo deberías usarla si la sub-cuenta quedó en estado <code>ERROR</code> o
          si recibiste indicación explícita de soporte.
        </p>
        <p style="text-align: left; margin: 0; font-size: 0.85rem; color: #6b7280;">
          Ingresa el <strong>subAccountId</strong> exacto que devolvió Binance.
        </p>
      `,
      input: 'text',
      inputLabel: 'Sub-account ID',
      inputPlaceholder: 'Ej: 1234567890',
      inputAttributes: {
        autocapitalize: 'off',
        autocorrect: 'off',
        inputmode: 'numeric',
      },
      inputValidator: (value) => {
        const trimmed = (value || '').trim();
        if (!trimmed) return 'Debes ingresar un subAccountId válido';
        if (!/^[A-Za-z0-9_-]+$/.test(trimmed))
          return 'Sub-account ID inválido (solo letras, números, guion y guion bajo)';
        return null;
      },
      showCancelButton: true,
      confirmButtonText: 'Eliminar sub-cuenta',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      width: '520px',
    });

    if (!subAccountId) return;
    const trimmed = (subAccountId as string).trim();

    // Confirmación adicional con el subAccountId tipeado para evitar typos.
    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Confirmar eliminación?',
      html: `
        <p style="text-align: left; margin: 0 0 8px 0; font-size: 0.85rem; color: #374151;">
          Vas a solicitar la eliminación de la sub-cuenta:
        </p>
        <code style="display: block; padding: 8px 10px; background: #f3f4f6; border-radius: 6px; font-size: 0.9rem; color: #b91c1c; font-weight: 700; word-break: break-all;">
          ${trimmed}
        </code>
        <p style="text-align: left; margin: 12px 0 0 0; font-size: 0.78rem; color: #b91c1c;">
          ⚠ Esta acción no se puede deshacer desde el panel.
        </p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
    });
    if (!confirm.isConfirmed) return;

    const ref = this.spinner.start();
    try {
      await this.walletAdminService.deleteBinanceSubAccount(trimmed);
      this.spinner.stop(ref);
      await Swal.fire({
        icon: 'success',
        title: 'Sub-cuenta eliminada',
        text: 'La solicitud de baja fue procesada correctamente.',
        confirmButtonColor: '#1e5a3f',
      });
      // Refresca el listado: la fila correspondiente puede haber
      // cambiado de estado tras el delete.
      await this.loadUsers();
    } catch (error: any) {
      this.spinner.stop(ref);
      const status = error?.status;
      const apiMsg = error?.error?.message;
      const binanceCode = error?.error?.binanceCode;
      let title = 'Error al eliminar';
      let text = apiMsg || 'No se pudo eliminar la sub-cuenta.';
      if (status === 404) {
        title = 'Sub-cuenta no encontrada';
        text = `No existe una sub-cuenta con ID "${trimmed}".`;
      } else if (status === 401 || status === 403) {
        title = 'Sin permisos';
        text = 'No tenés permiso para ejecutar esta acción.';
      } else if (status === 502) {
        title = 'Error de Binance';
        text = `${apiMsg || 'Error contra Binance'}${binanceCode ? ' (código ' + binanceCode + ')' : ''}`;
      }
      Swal.fire({
        icon: 'error',
        title,
        text,
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  /**
   * Limpia el setInterval del polling cuando se navega fuera del
   * componente. Sin esto, queda corriendo en memoria y haciendo HTTP
   * cada 5s aunque el operador ya no esté en la pantalla.
   */
  ngOnDestroy(): void {
    this.stopPrevalidacionPolling();
  }

  /**
   * Carga (o refresca) el header del dashboard. Sin spinner global porque
   * suele tardar ~50ms (DB local). Errores se loguean en consola sin
   * bloquear: el listado igual funciona aunque el dashboard falle.
   */
  async loadDashboard(): Promise<void> {
    try {
      this.dashboard = await this.walletAdminService.getDashboard();
    } catch (err) {
      console.error('Error cargando dashboard binance-link', err);
      this.dashboard = null;
    }
  }

  /**
   * Carga el timeline de snapshots del master para el sparkline. No
   * bloqueante: si falla, el sparkline no se pinta pero el resto del
   * dashboard funciona. Tras un deploy del backend el buffer arranca
   * vacio y este endpoint devuelve `snapshots: []` — manejado en el
   * template mostrando "Esperando datos".
   */
  async loadHistory(): Promise<void> {
    try {
      const res = await this.walletAdminService.getDashboardHistory();
      this.historySnapshots = res.snapshots;
      this.historyCapacity = res.capacity;
    } catch (err) {
      console.error('Error cargando history binance-link master', err);
      this.historySnapshots = [];
    }
  }

  /**
   * Carga el estado de la sincronización masiva de prevalidación KYC.
   * No bloqueante: si falla, la card se queda con "—" pero el resto del
   * dashboard sigue funcionando.
   */
  async loadPrevalidacion(): Promise<void> {
    try {
      this.prevalidacion = await this.walletAdminService.getPrevalidacionStatus();
      this.prevalidacionLoadError = null;
    } catch (err: any) {
      const status = err?.status;
      const apiMsg = err?.error?.message;
      const detail = apiMsg || err?.message || 'Error desconocido';
      // Mensaje contextual segun el tipo de error mas comun
      if (status === 404 || status === 0) {
        this.prevalidacionLoadError =
          `Endpoint /sync-prevalidacion/status no responde (HTTP ${status}). ` +
          `El backend puede no tener la última versión deployada.`;
      } else if (status === 500 && /relation|table.*doesn't exist|ER_NO_SUCH_TABLE/i.test(detail)) {
        this.prevalidacionLoadError =
          `La tabla binance_link_prevalidacion_jobs no existe en la BD. ` +
          `Aplicá el DDL antes de usar esta funcionalidad.`;
      } else {
        this.prevalidacionLoadError = `${detail} (HTTP ${status ?? '—'})`;
      }
      console.error('[Prevalidacion] Error cargando estado:', err);
      this.prevalidacion = null;
    } finally {
      this.prevalidacionLoading = false;
    }
  }

  /**
   * Click en "Sincronizar prevalidación". Defendido server-side con 409
   * si hay job activo, pero el botón ya está disabled mientras `isRunning`.
   */
  async onStartPrevalidacionSync(): Promise<void> {
    if (this.prevalidacion?.isRunning) return;
    try {
      const res = await this.walletAdminService.startPrevalidacionSync();
      Swal.fire({
        icon: 'success',
        title: 'Sincronización iniciada',
        text: `Encolados ${res.totalEnqueued} usuarios. El dashboard va a actualizar el progreso en vivo.`,
        timer: 2500,
        showConfirmButton: false,
      });
      await this.loadPrevalidacion();
      this.startPrevalidacionPolling();
    } catch (err: any) {
      const status = err?.status;
      const apiMsg = err?.error?.message;
      Swal.fire({
        icon: status === 409 ? 'warning' : 'error',
        title:
          status === 409 ? 'Ya hay una sincronización en curso' : 'Error',
        text: apiMsg || 'No se pudo iniciar la sincronización.',
        confirmButtonColor: '#1e5a3f',
      });
      // Refresh para que el botón refleje el estado real
      await this.loadPrevalidacion();
    }
  }

  /**
   * Arranca el polling cada 5s del estado del job. Cuando el job pase a
   * DONE / ERROR, el polling se auto-detiene en `tickPrevalidacionPolling`.
   */
  private startPrevalidacionPolling(): void {
    this.stopPrevalidacionPolling();
    this.prevalidacionPoll = setInterval(
      () => this.tickPrevalidacionPolling(),
      5000,
    );
  }

  private stopPrevalidacionPolling(): void {
    if (this.prevalidacionPoll) {
      clearInterval(this.prevalidacionPoll);
      this.prevalidacionPoll = null;
    }
  }

  /**
   * Tick del polling: refresca el estado y si terminó, detiene el
   * polling y refresca el listado de usuarios (los counters globales
   * cambiaron tras la corrida).
   */
  private async tickPrevalidacionPolling(): Promise<void> {
    await this.loadPrevalidacion();
    if (!this.prevalidacion?.isRunning) {
      this.stopPrevalidacionPolling();
      // Refresh del listado para que se vean las validations nuevas
      // que pueda haber pre-cargado en cada user.
      await this.loadUsers();
    }
  }

  /**
   * Helper: porcentaje de progress del job actual (0-100).
   */
  prevalidacionProgressPct(): number {
    const job = this.prevalidacion?.lastJob;
    if (!job || job.totalEnqueued <= 0) return 0;
    return Math.min(100, Math.round((job.totalProcessed / job.totalEnqueued) * 100));
  }

  /**
   * Helper: % de óptimos sobre pre-validados (para el card global).
   * Devuelve string "—" si no hay denominador.
   */
  prevalidacionOptimosPct(): string {
    const c = this.prevalidacion?.globalCounters;
    if (!c || c.preValidados <= 0) return '—';
    return ((c.optimos / c.preValidados) * 100).toFixed(1) + '%';
  }

  /**
   * Triggera un sync inmediato y refresca dashboard + listado.
   * Spinner global mientras dura (Binance puede tardar varios segundos).
   */
  async onSyncNow(): Promise<void> {
    if (this.isSyncing) return;
    this.isSyncing = true;
    const ref = this.spinner.start();
    try {
      this.dashboard = await this.walletAdminService.syncNow();
      // Refrescar history para incluir el snapshot recien capturado.
      await Promise.all([this.loadHistory(), this.loadUsers()]);
      Swal.fire({
        icon: 'success',
        title: 'Sincronizado',
        text: 'Datos actualizados desde Binance.',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err: any) {
      const apiMsg = err?.error?.message;
      Swal.fire({
        icon: 'error',
        title: 'Error de sincronizacion',
        text: apiMsg || 'No se pudo sincronizar con Binance.',
        confirmButtonColor: '#1e5a3f',
      });
    } finally {
      this.spinner.stop(ref);
      this.isSyncing = false;
    }
  }

  /**
   * Carga la lista admin de usuarios con (o sin) wallet, paginada y
   * filtrada por `q` + `hasWallet`. Es la fuente de verdad principal de
   * la vista — reemplaza `loadWallets()` que solo traia wallets.
   */
  async loadUsers(): Promise<void> {
    const ref = this.spinner.start();
    try {
      const filters: UsersWithWalletFilters = {
        page: this.currentPage,
        limit: this.pageSize,
        hasWallet: this.filterHasWallet,
      };
      const q = (this.filterQ || '').trim();
      if (q) filters.q = q;

      const response = await this.walletAdminService.listUsersWithWallet(filters);
      this.users = response.data;
      this.totalItems = response.meta.total;
      this.totalPages = response.meta.totalPages;
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo cargar el listado de usuarios.',
        confirmButtonColor: '#1e5a3f',
      });
    } finally {
      this.spinner.stop(ref);
    }
  }

  onPageChange(event: any): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex + 1;
    this.loadUsers();
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadUsers();
  }

  clearFilters(): void {
    this.filterQ = '';
    this.filterHasWallet = 'all';
    this.currentPage = 1;
    this.loadUsers();
  }

  /** Cards mobile: toggle por usuarioId (id estable). */
  toggleCard(usuarioId: number): void {
    this.expandedCardId = this.expandedCardId === usuarioId ? null : usuarioId;
  }

  /**
   * Convierte una fila `UserWithWallet` en `BinanceWallet` para los modals
   * que esperan ese tipo. Solo valido cuando `walletId !== null`.
   */
  private toBinanceWallet(u: UserWithWallet): BinanceWallet {
    return {
      id: u.walletId!,
      usuarioId: u.usuarioId,
      subAccountId: u.subAccountId!,
      subAccountEmail: u.subAccountEmail,
      tag: String(u.usuarioId),
      walletStatus: u.walletStatus!,
      kycStatus: u.kycStatus!,
      activeFlag: 1,
      createdByCognitoId: null,
      disabledReason: null,
      disabledAt: null,
      createdAt: u.walletCreatedAt!,
      updatedAt: u.lastBalanceSyncedAt ?? u.walletCreatedAt!,
      lastBalanceBtc: u.lastBalanceBtc,
      lastBalanceUsdt: u.lastBalanceUsdt,
      lastBalanceSyncedAt: u.lastBalanceSyncedAt,
      lastDepositAt: u.lastDepositAt,
      totalDepositsCount: u.totalDepositsCount,
      makerCommission: null,
      takerCommission: null,
      marginEnabled: 0,
      lastSyncError: null,
    };
  }

  getDateFormatted(date: any): string {
    return date
      ? moment(new Date(date)).tz('America/Santiago').format('lll')
      : 'No aplica';
  }

  /**
   * Formato corto para la fecha de aprobación de compliance: "07 may 2026".
   * Sin hora, locale en español. Si la fecha no es valida o es null,
   * devuelve "—".
   */
  formatApprovalDate(date: any): string {
    if (!date) return '—';
    const d = moment(new Date(date)).tz('America/Santiago');
    if (!d.isValid()) return '—';
    return d.locale('es').format('DD MMM YYYY');
  }

  /** "hace 2 minutos" / "hace 1 hora" para el indicador de freshness. */
  getRelativeTime(date: any): string {
    if (!date) return '—';
    return moment(new Date(date)).locale('es').fromNow();
  }

  /** Formato compacto de balance BTC: "0.00012345" o "—" si null. */
  formatBtc(balance: string | null): string {
    if (balance === null || balance === undefined) return '—';
    const n = Number(balance);
    if (isNaN(n)) return '—';
    if (n === 0) return '0';
    // Mostrar hasta 8 decimales pero recortar trailing zeros.
    return n.toFixed(8).replace(/\.?0+$/, '');
  }

  /**
   * `true` si la sub-cuenta tiene USDT pendiente (> 0). Permite resaltar
   * filas con saldo no centralizado. `"0.00000000"` y `null` retornan
   * `false` — se diferencian en el render del texto, no en el highlight.
   */
  hasPositiveSubUsdt(row: { lastBalanceUsdt?: string | null }): boolean {
    const raw = row?.lastBalanceUsdt;
    if (raw == null) return false;
    const n = Number(raw);
    return !isNaN(n) && n > 0;
  }

  /** Formato porcentaje de comisiones: "0.10%" o "—" si null. */
  formatCommission(value: string | null): string {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    if (isNaN(n)) return '—';
    return (n * 100).toFixed(2) + '%';
  }

  /**
   * Formato moneda fiat con miles + 2 decimales: "1,234,567.89".
   * Usado para USDT y total USD del master. `null`/`NaN` -> "—".
   * No agrega prefijo "$" — el template decide la unidad para evitar
   * confundir USDT con USD.
   */
  formatUsd(value: string | null): string {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    if (isNaN(n)) return '—';
    return n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // ─── Helpers persona / empresa ─────────────────────────────────
  // El backend solo devuelve usuarios persona (0) o empresa (1) con
  // tipo_id='RUT'. Estos helpers centralizan la decision de "que mostrar"
  // segun el tipo, asi el template no se llena de *ngIf encadenados.

  /** `true` si la fila representa una empresa (tipo_usuario_id = 1). */
  isEmpresa(row: UserWithWallet): boolean {
    return row.tipoUsuarioId === 1;
  }

  /**
   * Nombre a mostrar (siempre UPPERCASE para uniformidad visual entre
   * datos cargados con distinta capitalizacion):
   *  - persona: "NOMBRE APELLIDOS"
   *  - empresa: razon social (con fallback a "NOMBRE APELLIDOS" si falta)
   */
  displayName(row: UserWithWallet): string {
    const raw = this.isEmpresa(row) && row.razonSocial
      ? row.razonSocial
      : `${row.nombre} ${row.apellidos}`.trim();
    return raw.toUpperCase();
  }

  /**
   * Pais a mostrar. Para usuarios con `tipoId='RUT'` (los unicos del
   * listado actualmente) devolvemos "CHILE" explicitamente, en lugar del
   * codigo `paisResidencia` que puede ser "CL" o estar vacio. Si en el
   * futuro entran otros documentos al listado, mantenemos el fallback al
   * codigo del pais para que algo se renderice. Siempre uppercase para
   * uniformidad visual con el resto del listado.
   */
  displayPais(row: UserWithWallet): string {
    if (row.tipoId === 'RUT') return 'CHILE';
    return (row.paisResidencia || '—').toUpperCase();
  }

  /**
   * RUT a mostrar:
   *  - persona: `numeroId` (que es el RUT del titular)
   *  - empresa: `rutEmpresa` (NO `numeroId`, ese seria el RUT del
   *    representante legal y no es lo que el operador necesita ver)
   */
  displayRut(row: UserWithWallet): string {
    if (this.isEmpresa(row)) {
      return row.rutEmpresa || row.numeroId || '—';
    }
    return row.numeroId || '—';
  }

  // ─── Sparkline del saldo USDT ────────────────────────────────────
  // Renderizado inline en SVG, sin libreria externa. Computa el path
  // a partir de `historySnapshots` cada vez que cambia el array.

  /** Dimensiones del SVG (constantes — el viewBox las usa). */
  readonly sparkWidth = 220;
  readonly sparkHeight = 40;
  private readonly sparkPad = 2;

  /**
   * Convierte `historySnapshots` (orden DESC) en una cadena SVG `points`
   * para `<polyline>`. Filtra snapshots con `masterUsdtBalance` no null
   * y devuelve `''` si hay <2 puntos validos (en cuyo caso el template
   * no renderiza el chart).
   */
  get sparkPoints(): string {
    const series = this.sparkSeries;
    if (series.length < 2) return '';
    const min = Math.min(...series);
    const max = Math.max(...series);
    const range = max - min || 1; // evitar division por 0 si todos iguales
    const w = this.sparkWidth - this.sparkPad * 2;
    const h = this.sparkHeight - this.sparkPad * 2;
    return series
      .map((v, i) => {
        const x = this.sparkPad + (i / (series.length - 1)) * w;
        // Y invertido: max arriba (y bajo), min abajo (y alto)
        const y = this.sparkPad + h - ((v - min) / range) * h;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }

  /** `true` si tenemos al menos 2 puntos validos para pintar el chart. */
  get hasSparkData(): boolean {
    return this.sparkSeries.length >= 2;
  }

  /** Min y max del rango USDT graficado, para mostrar como eje. */
  get sparkMin(): number | null {
    const s = this.sparkSeries;
    return s.length ? Math.min(...s) : null;
  }
  get sparkMax(): number | null {
    const s = this.sparkSeries;
    return s.length ? Math.max(...s) : null;
  }

  /**
   * Convierte snapshots a array numerico ascendente (oldest -> newest)
   * para que el sparkline se lea left-to-right. Cachear via Angular
   * change detection: el getter se re-evalua solo cuando `historySnapshots`
   * cambia de referencia (loadHistory siempre asigna nueva ref).
   */
  private get sparkSeries(): number[] {
    return this.historySnapshots
      .filter((s) => s.masterUsdtBalance !== null)
      .map((s) => Number(s.masterUsdtBalance))
      .filter((n) => !Number.isNaN(n))
      .reverse(); // backend manda DESC, invertir para timeline left->right
  }

  /** Estilo del indicador de status del sync (badge en el header). */
  getSyncStatusBg(status: SyncStatus | undefined): string {
    if (status === 'OK') return '#dcfce7';
    if (status === 'RUNNING') return '#dbeafe';
    if (status === 'ERROR') return '#fecaca';
    return '#f3f4f6';
  }

  getSyncStatusColor(status: SyncStatus | undefined): string {
    if (status === 'OK') return '#15803d';
    if (status === 'RUNNING') return '#1e40af';
    if (status === 'ERROR') return '#b91c1c';
    return '#6b7280';
  }

  // Status helpers — aceptan `null` porque UserWithWallet puede traer wallet
  // o kyc null cuando el usuario todavia no tiene wallet asociada. El
  // template Angular en strict mode no puede inferir narrowing dentro de
  // un *ngIf, por eso aceptamos null aca y devolvemos fallback gris.
  getWalletStatusBg(status: WalletStatus | null): string {
    switch (status) {
      case 'ACTIVE':
        return '#16a34a';
      case 'KYC_PENDING':
        return '#eab308';
      case 'CREATING':
        return '#9ca3af';
      case 'BLOCKED':
        return '#fca5a5';
      case 'ERROR':
        return '#dc2626';
      default:
        return '#f3f4f6';
    }
  }

  getWalletStatusColor(status: WalletStatus | null): string {
    return status === 'BLOCKED' ? '#7f1d1d' : '#ffffff';
  }

  getKycStatusBg(status: KycStatus | null): string {
    switch (status) {
      case 'PASS':
        return '#dcfce7';
      case 'PENDING':
        return '#fef9c3';
      case 'REJECT':
        return '#fecaca';
      case 'RFI_REQUIRED':
        return '#fed7aa';
      case 'NONE':
      default:
        return '#f3f4f6';
    }
  }

  getKycStatusColor(status: KycStatus | null): string {
    switch (status) {
      case 'PASS':
        return '#15803d';
      case 'PENDING':
        return '#a16207';
      case 'REJECT':
        return '#b91c1c';
      case 'RFI_REQUIRED':
        return '#c2410c';
      case 'NONE':
      default:
        return '#6b7280';
    }
  }

  // ============================================================
  // Badge de prevalidación KYC interna
  // ============================================================
  // El badge sirve para que el operador sepa de un vistazo si puede
  // crearle la wallet Binance Link al usuario o si todavia hay algo que
  // resolver (datos OCR que no matchean, falta selfie, etc). La regla
  // de bucket replica la categorizacion del backend en
  // `BinanceLinkPrevalidacionService.processOneUser` y
  // `computeGlobalCounters`.

  /**
   * Devuelve el badge a renderizar para la fila. La regla:
   *  - `BLOQUEADO` si falta selfie o foto frontal — el revalidate va a
   *    fallar igual, no tiene sentido habilitar el boton.
   *  - `SIN_PREVALIDAR` si nunca se persistió validation (campo `null`).
   *  - `OPTIMO` si `faceMatch && ocrOverall === 'OK'`.
   *  - `NO_OPTIMO` si tiene validation pero no paso (face fail o OCR
   *    mismatch). Tooltip enumera las razones encontradas.
   *
   * `canRevalidate` indica si tiene sentido mostrar el boton de accion
   * individual al lado del badge. Es `true` para SIN_PREVALIDAR y
   * NO_OPTIMO; `false` para OPTIMO (no hay nada que mejorar) y para
   * BLOQUEADO (faltan archivos, hay que pedirselos al usuario).
   */
  prevalidationBadge(row: UserWithWallet): {
    status: 'OPTIMO' | 'NO_OPTIMO' | 'BLOQUEADO' | 'SIN_PREVALIDAR';
    label: string;
    icon: string;
    bg: string;
    color: string;
    tooltip: string;
    canRevalidate: boolean;
  } {
    const missingSelfie = !row.selfie || !row.selfie.trim();
    const missingFront = !row.fotoIdfrontal || !row.fotoIdfrontal.trim();
    if (missingSelfie || missingFront) {
      const missing = [
        missingSelfie ? 'selfie' : null,
        missingFront ? 'foto frontal' : null,
      ]
        .filter(Boolean)
        .join(' y ');
      return {
        status: 'BLOQUEADO',
        label: 'Bloqueado',
        icon: 'block',
        bg: '#fecaca',
        color: '#b91c1c',
        tooltip: `Falta ${missing}. El usuario debe subir los archivos antes de prevalidar.`,
        canRevalidate: false,
      };
    }
    const pv = row.prevalidation;
    if (!pv) {
      return {
        status: 'SIN_PREVALIDAR',
        label: 'Sin prevalidar',
        icon: 'pending',
        bg: '#f3f4f6',
        color: '#6b7280',
        tooltip:
          'Todavía no se le corrió la prevalidación. Click para validar este usuario ahora.',
        canRevalidate: true,
      };
    }
    if (pv.faceMatch && pv.ocrOverall === 'OK') {
      return {
        status: 'OPTIMO',
        label: 'Óptimo',
        icon: 'check_circle',
        bg: '#dcfce7',
        color: '#15803d',
        tooltip:
          `Face match + OCR OK. Validado ${this.getRelativeTime(pv.validatedAt)}` +
          (pv.faceSimilarity ? ` · similitud ${pv.faceSimilarity}` : ''),
        canRevalidate: false,
      };
    }
    // No óptimo — armar el detalle de qué falló para el tooltip.
    const reasons: string[] = [];
    if (!pv.faceMatch) {
      reasons.push(
        `face fail${pv.faceSimilarity ? ` (similitud ${pv.faceSimilarity})` : ''}`,
      );
    }
    if (pv.ocrOverall !== 'OK') reasons.push(`OCR ${pv.ocrOverall}`);
    if (pv.ocrDocumentExpired) reasons.push('documento vencido');
    return {
      status: 'NO_OPTIMO',
      label: 'No óptimo',
      icon: 'warning',
      bg: '#fed7aa',
      color: '#c2410c',
      tooltip:
        `${reasons.join(' · ')}. Validado ${this.getRelativeTime(pv.validatedAt)}.` +
        ' Click para volver a prevalidar (10 min de cache anti-doble-click).',
      canRevalidate: true,
    };
  }

  /**
   * Dispara revalidate para un sólo usuario (reusa el endpoint que ya
   * existe para el modal). Refresca la fila después porque el endpoint
   * persiste en `binance_link_kyc_validations` y al recargar el listado
   * el badge va a reflejar el resultado fresh.
   *
   * Cache backend: 10 min — clicks repetidos en ese intervalo devuelven
   * el mismo resultado sin pegarle a AWS de nuevo. El badge se actualiza
   * igual porque la fila persistida es la misma.
   */
  async onRevalidateUser(row: UserWithWallet): Promise<void> {
    if (this.revalidatingUserId === row.usuarioId) return;
    this.revalidatingUserId = row.usuarioId;
    try {
      await this.walletAdminService.revalidateKyc(row.usuarioId);
      await this.loadUsers();
      Swal.fire({
        icon: 'success',
        title: 'Prevalidación actualizada',
        text: 'El badge refleja el resultado más reciente.',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err: any) {
      const status = err?.status;
      const apiMsg = err?.error?.message;
      Swal.fire({
        icon: 'error',
        title: 'Error prevalidando',
        text:
          apiMsg ||
          (status === 400
            ? 'Faltan datos del usuario (selfie o foto frontal).'
            : 'No se pudo correr la prevalidación.'),
        confirmButtonColor: '#1e5a3f',
      });
    } finally {
      this.revalidatingUserId = null;
    }
  }

  // ============================================================
  // Checklist KYC pre-creación
  // ============================================================
  // Binance Link no permite operar con la sub-cuenta hasta que pase
  // KYC. Antes de crear validamos en el modal que el `User` tenga
  // todos los campos que `share-kyc-data` va a pedir (ver
  // `binance-link-kyc-plan.md` en el back), para no crear subs muertas.

  /**
   * Firma una S3 key del User (`fotoIdfrontal`, `fotoIdPosterior`,
   * `selfie`) y devuelve una URL pre-signed que el browser pueda
   * cargar en el `<img>` del modal.
   *
   * Migración (post-VULN-01): ya NO firma con el SDK de AWS en el
   * cliente — pide la URL al backend vía `S3UrlService`. El cambio de
   * sync a async hace que `buildKycChecklist` también sea async; la
   * llamada se hace una vez al abrir el modal y los thumbnails quedan
   * cacheados en el `S3UrlService` durante la sesión.
   *
   * Devuelve `null` si la key viene vacía o el backend responde 404.
   */
  private signKycPhoto(
    key: string | null | undefined,
  ): Promise<string | null> {
    return this.s3UrlService.getReadUrl(key);
  }

  /**
   * Devuelve el split OK/falta de los datos KYC del usuario.
   *
   * Verdad fuente: la doc de Binance KYC SaaS define cada campo como
   * mandatory/optional en el endpoint `share-kyc-data`
   * (https://developers.binance.com/docs/binance_link/kyc-saas/kyc-integration).
   * Auditoría 2026-05-08 confirmó:
   *   - basicInfo: middleName/postalCode opcionales; resto obligatorios.
   *   - identityInfo: idSubType, idExpiry, gender, backFileUrl,
   *     personalNumber, issuingPlace, issuingDate, registrationNumber,
   *     stateCode, idUsState, issuingAuthority — todos OPCIONALES.
   *
   * Estructura:
   *  - `ok` lista los datos textuales cargados (sean required u opcional).
   *  - `missing` lista los textuales OBLIGATORIOS no cargados (rojo,
   *    bloquean creación).
   *  - `missingOptional` lista los textuales OPCIONALES no cargados +
   *    fotos opcionales no cargadas (nota gris, NO bloquean).
   *  - `photos` es siempre un array de 3 entradas (frontal, dorso,
   *    selfie) con URL pre-signed o `null` y un flag `required`.
   *  - `isReady` = todos los requeridos cargados (texto + fotos req).
   *
   * "Tiene el dato" = string non-null y no vacio tras `.trim()`. Los
   * campos NOT NULL en BD pueden venir como `''` en usuarios legacy.
   */
  private async buildKycChecklist(user: UserWithWallet): Promise<{
    ok: { label: string; value: string }[];
    missing: string[];
    missingOptional: string[];
    photos: { label: string; url: string | null; required: boolean }[];
    totalMissing: number;
    isReady: boolean;
    /**
     * `true` si el backend tiene Foto ID frontal Y Selfie cargadas.
     * Sin estas dos NO se puede ejecutar la revalidacion (el endpoint
     * baja del S3 y manda al KYC: si una falta, 400). El frontend
     * deshabilita el boton "Revalidar" cuando este flag es false.
     */
    canRevalidate: boolean;
  }> {
    const has = (v: string | null | undefined): boolean =>
      typeof v === 'string' && v.trim().length > 0;

    // `required: false` = Binance acepta el payload sin este campo. Si no
    // está cargado va a `missingOptional` (nota informativa) en lugar de
    // `missing` (bloqueador).
    const items: {
      label: string;
      raw: string | null;
      display: string;
      required: boolean;
    }[] = [
      { label: 'Nombre',          raw: user.nombre,           display: user.nombre,                              required: true },
      { label: 'Apellidos',       raw: user.apellidos,        display: user.apellidos,                           required: true },
      { label: 'Nacimiento',      raw: user.fechaNacimiento,  display: this.formatKycDate(user.fechaNacimiento), required: true },
      { label: 'Nacionalidad',    raw: user.nacionalidad,     display: user.nacionalidad,                        required: true },
      { label: 'País residencia', raw: user.paisResidencia,   display: user.paisResidencia,                      required: true },
      { label: 'Ciudad',          raw: user.ciudadResidencia, display: user.ciudadResidencia,                    required: true },
      { label: 'Dirección',       raw: user.direccion,        display: '✓',                                      required: true },
      { label: 'RUT',             raw: user.numeroId,         display: user.numeroId,                            required: true },
      // Opcionales según spec Binance — no bloquean.
      { label: 'Sexo',            raw: user.sexo,             display: user.sexo,                                required: false },
      { label: 'Fecha exp. doc.', raw: user.fechaExp,         display: this.formatKycDate(user.fechaExp),        required: false },
    ];

    const ok: { label: string; value: string }[] = [];
    const missing: string[] = [];
    const missingOptional: string[] = [];
    for (const it of items) {
      if (has(it.raw)) ok.push({ label: it.label, value: it.display });
      else if (it.required) missing.push(it.label);
      else missingOptional.push(it.label);
    }

    // Las columnas `foto_id_frontal/posterior/selfie` del User guardan
    // S3 keys relativas (ej. `CL/<uuid>/documentoFrontal.jpg`), no URLs
    // resolvibles. Post-VULN-01 las firmas las hace el backend vía
    // `S3UrlService.getReadUrl()` — ya no hay creds AWS en el cliente.
    // El cache interno del servicio evita round-trips repetidos sobre
    // la misma key.
    //
    // `backFileUrl` es opcional según la spec; lo dejamos visible en la
    // tira pero con styling de opcional cuando falta — si la doc cambia
    // o Binance rechaza algún tipo de documento sin dorso, basta con
    // flippear `required: true` aquí.
    const photoSpecs: { label: string; key: string | null; required: boolean }[] = [
      { label: 'Foto ID frontal', key: user.fotoIdfrontal,   required: true },
      { label: 'Foto ID dorso',   key: user.fotoIdPosterior, required: false },
      { label: 'Selfie',          key: user.selfie,          required: true },
    ];
    const photoUrls = await Promise.all(
      photoSpecs.map((p) => this.signKycPhoto(p.key)),
    );
    const photos = photoSpecs.map((p, i) => ({
      label: p.label,
      url: photoUrls[i],
      required: p.required,
    }));
    const missingPhotosRequired = photos.filter((p) => !p.url && p.required).length;
    for (const p of photos) {
      if (!p.url && !p.required) missingOptional.push(p.label);
    }

    // `missingOptional` NO suma en `totalMissing` — son informativos y
    // no bloquean la creación. `isReady` sólo evalúa requeridos.
    const totalMissing = missing.length + missingPhotosRequired;

    // Para revalidar necesitamos sí o sí selfie + foto frontal — el
    // backend baja ambas del S3 para mandarlas al KYC. Si falta una, el
    // frontend deshabilita el boton "Revalidar" y comunica el motivo.
    const frontPhoto = photos.find((p) => p.label === 'Foto ID frontal');
    const selfiePhoto = photos.find((p) => p.label === 'Selfie');
    const canRevalidate = !!frontPhoto?.url && !!selfiePhoto?.url;

    return {
      ok,
      missing,
      missingOptional,
      photos,
      totalMissing,
      isReady: totalMissing === 0,
      canRevalidate,
    };
  }

  /**
   * `YYYY-MM-DD` para `fechaNacimiento` / `fechaExp` en el checklist.
   * Devuelve `''` si la fecha no parsea — el caller lo descarta como
   * faltante via `has(...)` en `buildKycChecklist`.
   */
  private formatKycDate(iso: string | null): string {
    if (!iso) return '';
    const d = moment(new Date(iso));
    return d.isValid() ? d.format('YYYY-MM-DD') : '';
  }

  /**
   * Construye el HTML del modal de creación: header del user, grilla de
   * dos columnas OK/FALTAN para los 10 datos textuales, y tira de
   * thumbnails para los 3 documentos (frontal, dorso, selfie).
   *
   * Sólo se usa para personas — las empresas cortocircuitan antes en
   * `openCreateWalletModal`.
   *
   * Las URLs de las fotos vienen de S3 (campos `fotoIdfrontal`,
   * `fotoIdPosterior`, `selfie` del User). Si el bucket está privado el
   * `<img>` falla a cargar; el `onerror` esconde la imagen y muestra el
   * placeholder "no disponible" para que el operador no lo confunda con
   * dato faltante.
   */
  private buildCreateWalletModalHtml(
    user: UserWithWallet,
    kyc: {
      ok: { label: string; value: string }[];
      missing: string[];
      missingOptional: string[];
      photos: { label: string; url: string | null; required: boolean }[];
      totalMissing: number;
      isReady: boolean;
      canRevalidate: boolean;
    },
  ): string {
    const okHtml = kyc.ok
      .map(
        (i) => `
          <li style="font-size: 0.72rem; padding: 2px 0; color: #374151;">
            <span style="color: #6b7280;">${i.label} ·</span>
            <strong style="color: #15803d;">${i.value}</strong>
          </li>`,
      )
      .join('');

    const missingHtml = kyc.missing.length
      ? kyc.missing
          .map(
            (label) => `
              <li style="font-size: 0.72rem; padding: 2px 0; color: #b91c1c; font-weight: 600;">
                ${label}
              </li>`,
          )
          .join('')
      : `<li style="font-size: 0.72rem; padding: 2px 0; color: #15803d;">— ninguno —</li>`;

    // Thumbnails de los 3 documentos. `target=_blank` para abrir la
    // imagen completa. El `onerror` swap-ea la imagen rota por un
    // placeholder rojo "no se pudo cargar" — ese caso es un bug de
    // bucket policy, no un dato faltante.
    const photosHtml = kyc.photos
      .map((p) => {
        if (p.url) {
          return `
            <div style="flex: 1; min-width: 0; text-align: center;">
              <p style="margin: 0 0 4px 0; font-size: 0.68rem; font-weight: 600; color: #6b7280;">${p.label}</p>
              <a href="${p.url}" target="_blank" rel="noopener" style="display: block; text-decoration: none;">
                <img src="${p.url}" alt="${p.label}"
                     style="width: 100%; max-width: 140px; height: 95px; object-fit: cover; border-radius: 6px; border: 2px solid #15803d; cursor: zoom-in;"
                     onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
                <div style="display: none; width: 100%; max-width: 140px; height: 95px; background: #fee2e2; border: 2px dashed #fca5a5; border-radius: 6px; align-items: center; justify-content: center; margin: 0 auto;">
                  <span style="font-size: 0.65rem; color: #b91c1c; text-align: center; padding: 0 6px;">No se pudo cargar</span>
                </div>
              </a>
              <p style="margin: 4px 0 0 0; font-size: 0.65rem; color: #15803d; font-weight: 700;">✓ Cargada · click para ampliar</p>
            </div>`;
        }
        // Foto faltante: distinguir requerida (rojo, bloquea) de
        // opcional (gris, informativo). `backFileUrl` es opcional según
        // la spec Binance — el operador no debería preocuparse si falta.
        if (p.required) {
          return `
            <div style="flex: 1; min-width: 0; text-align: center;">
              <p style="margin: 0 0 4px 0; font-size: 0.68rem; font-weight: 600; color: #6b7280;">${p.label}</p>
              <div style="width: 100%; max-width: 140px; height: 95px; background: #fef2f2; border: 2px dashed #fca5a5; border-radius: 6px; display: flex; align-items: center; justify-content: center; margin: 0 auto;">
                <span style="font-size: 0.78rem; color: #b91c1c; font-weight: 700;">FALTA</span>
              </div>
              <p style="margin: 4px 0 0 0; font-size: 0.65rem; color: #b91c1c; font-weight: 700;">✗ No cargada</p>
            </div>`;
        }
        return `
          <div style="flex: 1; min-width: 0; text-align: center;">
            <p style="margin: 0 0 4px 0; font-size: 0.68rem; font-weight: 600; color: #6b7280;">${p.label}</p>
            <div style="width: 100%; max-width: 140px; height: 95px; background: #f9fafb; border: 2px dashed #d1d5db; border-radius: 6px; display: flex; align-items: center; justify-content: center; margin: 0 auto;">
              <span style="font-size: 0.7rem; color: #9ca3af; font-weight: 600; text-align: center; padding: 0 6px;">Opcional<br/>no cargada</span>
            </div>
            <p style="margin: 4px 0 0 0; font-size: 0.65rem; color: #6b7280; font-style: italic;">opcional · no bloquea</p>
          </div>`;
      })
      .join('');

    const footer = kyc.isReady
      ? `
        <p style="margin: 12px 0 0 0; padding: 8px 10px; background: #dcfce7; border-radius: 6px; font-size: 0.75rem; color: #15803d;">
          ✓ El usuario cumple los requisitos KYC. Tras crear vamos a intentar obtener su dirección USDT (TRC20).
        </p>`
      : `
        <p style="margin: 12px 0 0 0; padding: 8px 10px; background: #fef3c7; border-radius: 6px; font-size: 0.75rem; color: #92400e;">
          ⚠ Faltan ${kyc.totalMissing} dato(s) — el usuario debe completarlos en su perfil antes de crear la wallet.
        </p>`;

    return `
      <div style="text-align: left; font-size: 0.85rem; color: #374151;">
        <p style="margin: 0 0 6px 0;">
          <strong>${this.displayName(user)}</strong> · #${user.usuarioId}
          <span style="display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: 4px; background: #dbeafe; color: #1e40af; font-size: 0.7rem; font-weight: 700;">PERSONA</span>
        </p>
        <p style="margin: 0; font-size: 0.78rem; color: #6b7280;">
          ${user.email} · RUT ${this.displayRut(user)}
        </p>

        <div style="margin-top: 12px; padding-top: 10px; border-top: 1px solid #e5e7eb;">
          <p style="margin: 0 0 8px 0; font-weight: 700; font-size: 0.78rem; color: #374151;">Requisitos KYC Binance</p>
          <div style="display: flex; gap: 14px;">
            <div style="flex: 1; min-width: 0;">
              <p style="margin: 0 0 4px 0; font-size: 0.7rem; font-weight: 700; color: #15803d;">✓ DATOS OK (${kyc.ok.length})</p>
              <ul style="margin: 0; padding-left: 0; list-style: none;">${okHtml}</ul>
            </div>
            <div style="flex: 1; min-width: 0; border-left: 1px solid #e5e7eb; padding-left: 14px;">
              <p style="margin: 0 0 4px 0; font-size: 0.7rem; font-weight: 700; color: #b91c1c;">✗ FALTAN (${kyc.missing.length})</p>
              <ul style="margin: 0; padding-left: 0; list-style: none;">${missingHtml}</ul>
            </div>
          </div>
          ${kyc.missingOptional.length
            ? `<p style="margin: 8px 0 0 0; font-size: 0.7rem; color: #6b7280; font-style: italic;">
                 Opcionales no cargados (no bloquean): ${kyc.missingOptional.join(', ')}
               </p>`
            : ''}
        </div>

        <div style="margin-top: 14px; padding-top: 10px; border-top: 1px solid #e5e7eb;">
          <p style="margin: 0 0 8px 0; font-weight: 700; font-size: 0.78rem; color: #374151;">Documentos</p>
          <div style="display: flex; gap: 10px; align-items: flex-start;">
            ${photosHtml}
          </div>
        </div>

        <div style="margin-top: 14px; padding-top: 10px; border-top: 1px solid #e5e7eb;">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; gap: 10px; flex-wrap: wrap;">
            <p style="margin: 0; font-weight: 700; font-size: 0.78rem; color: #374151;">
              Revalidación de identidad
              <span style="display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: 4px; background: #fee2e2; color: #b91c1c; font-size: 0.65rem; font-weight: 700;">REQUERIDO</span>
            </p>
            ${kyc.canRevalidate
              ? `<button id="btn-revalidate-kyc" type="button" style="padding: 6px 12px; border: 1px solid #1e5a3f; background: #1e5a3f; color: #fff; border-radius: 6px; font-size: 0.72rem; font-weight: 600; cursor: pointer;">
                   Revalidar contra documento
                 </button>`
              : `<button id="btn-revalidate-kyc" type="button" disabled style="padding: 6px 12px; border: 1px solid #d1d5db; background: #f3f4f6; color: #9ca3af; border-radius: 6px; font-size: 0.72rem; font-weight: 600; cursor: not-allowed;" title="Falta selfie y/o foto frontal">
                   No se puede revalidar
                 </button>`}
          </div>
          <div id="revalidation-result" style="font-size: 0.72rem; color: #92400e; padding: 8px 10px; background: #fef3c7; border-radius: 6px; font-style: normal;">
            ${kyc.canRevalidate
              ? `⚠ Obligatorio antes de crear: re-corre OCR fresh y facial match (selfie ↔ documento). El botón "Crear wallet" queda deshabilitado hasta que la revalidación pase.`
              : `✗ No se puede revalidar — falta cargar ${
                  !kyc.photos.find((p) => p.label === 'Foto ID frontal')?.url
                    ? 'foto frontal del documento'
                    : ''
                }${
                  !kyc.photos.find((p) => p.label === 'Foto ID frontal')?.url &&
                  !kyc.photos.find((p) => p.label === 'Selfie')?.url
                    ? ' y '
                    : ''
                }${
                  !kyc.photos.find((p) => p.label === 'Selfie')?.url
                    ? 'selfie'
                    : ''
                } en el perfil del usuario.`}
          </div>
        </div>

        ${footer}
      </div>
    `;
  }

  // ============================================================
  // Revalidación de identidad (re-OCR + facial match) dentro del modal
  // ============================================================
  // Cablea el botón "Revalidar contra documento" del modal a un handler
  // que dispara la revalidación contra el backend. SweetAlert2 no soporta
  // botones interactivos custom de forma nativa; lo hacemos con
  // event listeners en `didOpen` después de que el HTML está montado.

  /**
   * Wirea el click handler del botón de revalidar y lo deja listo para
   * cuando el operador clickee. Manejamos el caso re-render (`Swal.update`)
   * removiendo y re-attachando el listener en cada apertura.
   */
  private wireRevalidationButton(user: UserWithWallet): void {
    const btn = document.getElementById(
      'btn-revalidate-kyc',
    ) as HTMLButtonElement | null;
    if (!btn) return;
    // Evitar double-binding si Swal re-renderiza
    if (btn.dataset['wired'] === '1') return;
    btn.dataset['wired'] = '1';
    btn.addEventListener('click', () => this.runRevalidation(user));
  }

  /**
   * Al abrir el modal, busca la ULTIMA revalidación del usuario en DB.
   * Si hay, la muestra como punto de partida con la fecha en que se hizo
   * y aplica el toggle del botón "Crear wallet" según el resultado.
   *
   * NO falla el modal si la consulta tira error: en el peor caso queda
   * el placeholder original "⚠ Obligatorio antes de crear..." y el
   * operador puede clickear "Revalidar" igual.
   */
  private async preloadLatestRevalidation(user: UserWithWallet): Promise<void> {
    try {
      const latest = await this.walletAdminService.getLatestKycValidation(
        user.usuarioId,
      );
      if (!latest) return;
      const resultDiv = document.getElementById('revalidation-result');
      if (resultDiv) {
        resultDiv.innerHTML = this.renderRevalidationResult(latest);
        resultDiv.style.background = 'transparent';
        resultDiv.style.padding = '0';
      }
      const btn = document.getElementById(
        'btn-revalidate-kyc',
      ) as HTMLButtonElement | null;
      if (btn) btn.textContent = 'Revalidar de nuevo';
      // Aplicar el mismo criterio de habilitación que post-revalidación
      // fresh: face.isMatch + ocr.overall=OK.
      const canCreate = latest.face.isMatch && latest.ocr.overall === 'OK';
      if (canCreate) {
        this.setConfirmDisabled(false);
      } else {
        const reasons: string[] = [];
        if (!latest.face.isMatch) reasons.push('rostro no coincide');
        if (latest.ocr.overall !== 'OK')
          reasons.push('datos no coinciden con documento');
        this.setConfirmDisabled(
          true,
          `No se puede crear: ${reasons.join(', ')}`,
        );
      }
    } catch (err) {
      // Silencioso — el operador puede igualmente clickear "Revalidar"
      // y eso disparará la lógica con manejo de errores completo.
      console.warn('[binance-link] preload validacion KYC fallo', err);
    }
  }

  /**
   * Habilita / deshabilita el botón "Crear wallet" del Swal abierto. La
   * revalidación KYC es obligatoria: este helper centraliza el toggle del
   * estado disabled + label para no esparcir DOM mutations en el flujo.
   *
   * @param disabled true para bloquear, false para habilitar
   * @param label texto del botón (default: "Crear wallet" cuando habilitado,
   *   "Crear wallet (revalidar primero)" cuando bloqueado)
   */
  private setConfirmDisabled(disabled: boolean, label?: string): void {
    const btn = Swal.getConfirmButton();
    if (!btn) return;
    btn.disabled = disabled;
    btn.style.opacity = disabled ? '0.5' : '1';
    btn.style.cursor = disabled ? 'not-allowed' : 'pointer';
    btn.textContent = label
      ?? (disabled ? 'Crear wallet (revalidar primero)' : 'Crear wallet');
  }

  /**
   * Dispara la revalidación contra el backend y rendea el resultado en
   * `#revalidation-result`. Maneja errores tipados (400/404/502) con
   * mensajes específicos para que el operador entienda qué pasó (ej.
   * "selfie no detectada" vs "documento ilegible").
   */
  private async runRevalidation(user: UserWithWallet): Promise<void> {
    const btn = document.getElementById(
      'btn-revalidate-kyc',
    ) as HTMLButtonElement | null;
    const resultDiv = document.getElementById('revalidation-result');
    if (!btn || !resultDiv) return;

    btn.disabled = true;
    btn.textContent = 'Revalidando...';
    btn.style.opacity = '0.6';
    btn.style.cursor = 'not-allowed';
    resultDiv.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px; padding: 8px 0; color: #6b7280;">
        <span style="display: inline-block; width: 14px; height: 14px; border: 2px solid #d1d5db; border-top-color: #1e5a3f; border-radius: 50%; animation: revalidate-spin 0.8s linear infinite;"></span>
        <span style="font-size: 0.72rem;">Re-corriendo OCR + facial match (puede tardar 5-15s)...</span>
      </div>
      <style>@keyframes revalidate-spin { to { transform: rotate(360deg); } }</style>
    `;

    try {
      const result = await this.walletAdminService.revalidateKyc(user.usuarioId);
      resultDiv.innerHTML = this.renderRevalidationResult(result);
      btn.textContent = result.fromCache
        ? 'Revalidar (cache, click para forzar tras 10min)'
        : 'Revalidar de nuevo';

      // Política: habilitar "Crear wallet" SOLO si tanto la verificación
      // facial como el match OCR pasan. Esto garantiza dos cosas:
      //   1. La selfie y el documento son de la misma persona.
      //   2. La data registrada en BD coincide con el documento físico
      //      (el payload que mandaremos a Binance KYC SaaS no va a fallar
      //      por mismatch de datos).
      // documentExpired NO bloquea — Binance acepta `idExpiry` opcional.
      const canCreate = result.face.isMatch && result.ocr.overall === 'OK';
      if (canCreate) {
        this.setConfirmDisabled(false);
      } else {
        const reasons: string[] = [];
        if (!result.face.isMatch) reasons.push('rostro no coincide');
        if (result.ocr.overall !== 'OK') reasons.push('datos no coinciden con documento');
        this.setConfirmDisabled(
          true,
          `No se puede crear: ${reasons.join(', ')}`,
        );
      }
    } catch (err: any) {
      const status = err?.status;
      const apiMsg = err?.error?.message;
      let title = 'No se pudo revalidar';
      let detail = apiMsg || 'Error desconocido contra el KYC backend.';
      if (status === 400 && err?.error?.missing) {
        title = 'Datos faltantes';
        detail = `Le faltan al usuario: ${(err.error.missing as string[]).join(', ')}.`;
      } else if (status === 502) {
        title = 'KYC backend devolvió error';
        detail = err?.error?.kycMessage || apiMsg || 'Verifica logs del KYC backend.';
      }
      resultDiv.innerHTML = `
        <div style="margin-top: 4px; padding: 8px 10px; background: #fef2f2; border: 1px solid #fca5a5; border-radius: 6px;">
          <p style="margin: 0; font-size: 0.75rem; font-weight: 700; color: #b91c1c;">⚠ ${title}</p>
          <p style="margin: 2px 0 0 0; font-size: 0.7rem; color: #991b1b; font-style: normal;">${detail}</p>
        </div>
      `;
      btn.textContent = 'Reintentar';
      // Falla de revalidación: mantener bloqueado el create — no podemos
      // garantizar que el payload llegue OK a Binance.
      this.setConfirmDisabled(true, 'Crear wallet (revalidar primero)');
    } finally {
      btn.disabled = false;
      btn.style.opacity = '1';
      btn.style.cursor = 'pointer';
    }
  }

  /**
   * Construye el HTML del resultado de revalidación: bloque facial +
   * tabla de campos OCR vs User. Estilos inline porque vive dentro del
   * Swal (que aísla los estilos del componente).
   */
  private renderRevalidationResult(
    res: import('../../interfaces/binance-wallet.interface').RevalidateKycResponse,
  ): string {
    const cacheTag = res.fromCache
      ? ` <span style="font-size: 0.65rem; color: #6b7280; font-weight: 400;">(cache)</span>`
      : '';

    // Bloque facial
    const faceColor = res.face.isMatch ? '#15803d' : '#b91c1c';
    const faceBg = res.face.isMatch ? '#dcfce7' : '#fee2e2';
    const faceLabel = res.face.isMatch ? '✓ MATCH' : '✗ NO MATCH';
    const faceBlock = `
      <div style="padding: 8px 10px; background: ${faceBg}; border-radius: 6px; margin-bottom: 8px;">
        <div style="display: flex; justify-content: space-between; align-items: center; gap: 10px;">
          <span style="font-size: 0.72rem; color: #374151;">
            <strong>Facial (selfie ↔ documento):</strong>
          </span>
          <span style="font-size: 0.7rem; font-weight: 700; color: ${faceColor};">
            ${faceLabel} · ${res.face.similarity.toFixed(1)}% (umbral ${res.face.threshold}%)
          </span>
        </div>
        ${!res.face.sourceFaceDetected || !res.face.targetFaceDetected
          ? `<p style="margin: 4px 0 0 0; font-size: 0.65rem; color: #b91c1c;">
              ⚠ ${!res.face.sourceFaceDetected ? 'No se detectó rostro en la selfie.' : ''}
              ${!res.face.targetFaceDetected ? 'No se detectó rostro en el documento.' : ''}
            </p>`
          : ''}
      </div>
    `;

    // Tabla de campos OCR vs User
    const fieldRows = Object.entries(res.ocr.fields)
      .map(([key, diff]) => {
        const matchIcon = diff.match
          ? '<span style="color: #15803d; font-weight: 700;">✓</span>'
          : '<span style="color: #b91c1c; font-weight: 700;">✗</span>';
        const labelMap: Record<string, string> = {
          numeroId: 'RUT',
          nombre: 'Nombre',
          apellidos: 'Apellidos',
          fechaNacimiento: 'Nacimiento',
          sexo: 'Sexo',
          nacionalidad: 'Nacionalidad',
        };
        const label = labelMap[key] ?? key;
        const userVal = diff.user ?? '<em style="color:#9ca3af;">(vacío)</em>';
        const ocrVal = diff.ocr ?? '<em style="color:#9ca3af;">(no leído)</em>';
        return `
          <tr style="border-bottom: 1px solid #f3f4f6;">
            <td style="padding: 4px 6px; font-size: 0.7rem; color: #6b7280;">${label}</td>
            <td style="padding: 4px 6px; font-size: 0.7rem; color: #374151;">${userVal}</td>
            <td style="padding: 4px 6px; font-size: 0.7rem; color: #374151;">${ocrVal}</td>
            <td style="padding: 4px 6px; text-align: center;">${matchIcon}</td>
          </tr>
        `;
      })
      .join('');

    const overallColor =
      res.ocr.overall === 'OK'
        ? '#15803d'
        : res.ocr.overall === 'PARTIAL'
          ? '#a16207'
          : '#b91c1c';
    const overallLabel =
      res.ocr.overall === 'OK'
        ? '✓ Todos los campos coinciden'
        : res.ocr.overall === 'PARTIAL'
          ? '⚠ Hay diferencias entre OCR y registro'
          : '✗ OCR no pudo leer datos comparables';

    const expiredWarning = res.ocr.documentExpired
      ? `<p style="margin: 6px 0 0 0; font-size: 0.7rem; color: #b91c1c;">
           ⚠ Documento vencido al día de hoy (Binance acepta payload sin idExpiry, no bloquea).
         </p>`
      : '';

    // El idExpiry capturado por el OCR es valioso aunque User.fechaExp
    // esté vacío: cuando se construya el payload Binance KYC SaaS
    // (Fase 2), incluirlo en `identityInfo.idExpiry` ayuda al review.
    const idExpiryRow = res.ocr.idExpiry
      ? `<p style="margin: 4px 0 0 0; font-size: 0.65rem; color: #15803d;">
           ✓ Vencimiento documento (del OCR): <strong>${res.ocr.idExpiry}</strong>
           — se incluirá en el payload a Binance.
         </p>`
      : '';

    // Veredicto al tope: si pasa face match Y los datos del User coinciden
    // con el documento, está apto para crear (y para mandar a Binance KYC
    // SaaS sin riesgo de mismatch de datos). Si alguno falla, NO apto.
    const aptForCreate = res.face.isMatch && res.ocr.overall === 'OK';
    const verdict = aptForCreate
      ? `<div style="margin-bottom: 8px; padding: 8px 10px; background: #dcfce7; border: 1px solid #86efac; border-radius: 6px;">
           <p style="margin: 0; font-size: 0.78rem; font-weight: 700; color: #15803d;">
             ✓ APTO PARA CREAR — identidad confirmada y datos coinciden con el documento
           </p>
         </div>`
      : `<div style="margin-bottom: 8px; padding: 8px 10px; background: #fee2e2; border: 1px solid #fca5a5; border-radius: 6px;">
           <p style="margin: 0; font-size: 0.78rem; font-weight: 700; color: #b91c1c;">
             ✗ NO APTO — ${!res.face.isMatch ? 'rostro no coincide' : ''}${!res.face.isMatch && res.ocr.overall !== 'OK' ? ' · ' : ''}${res.ocr.overall !== 'OK' ? 'datos no coinciden con documento' : ''}
           </p>
           <p style="margin: 4px 0 0 0; font-size: 0.7rem; color: #991b1b;">
             Mandar a Binance ahora resultaría en rechazo del KYC. Revisar diferencias y corregir antes de crear.
           </p>
         </div>`;

    return `
      <div style="font-style: normal;">
        ${verdict}
        <p style="margin: 0 0 6px 0; font-size: 0.7rem; color: #6b7280;">
          Validado: ${new Date(res.validatedAt).toLocaleString('es-CL')}${cacheTag}
        </p>
        ${faceBlock}
        <div>
          <p style="margin: 0 0 4px 0; font-size: 0.72rem; color: ${overallColor}; font-weight: 700;">
            ${overallLabel}
          </p>
          <table style="width: 100%; border-collapse: collapse; font-size: 0.7rem;">
            <thead>
              <tr style="background: #f9fafb;">
                <th style="padding: 4px 6px; text-align: left; font-size: 0.65rem; color: #6b7280; font-weight: 600;">Campo</th>
                <th style="padding: 4px 6px; text-align: left; font-size: 0.65rem; color: #6b7280; font-weight: 600;">User (BD)</th>
                <th style="padding: 4px 6px; text-align: left; font-size: 0.65rem; color: #6b7280; font-weight: 600;">OCR fresh</th>
                <th style="padding: 4px 6px; text-align: center; font-size: 0.65rem; color: #6b7280; font-weight: 600;">Match</th>
              </tr>
            </thead>
            <tbody>${fieldRows}</tbody>
          </table>
          ${res.ocr.confidence != null
            ? `<p style="margin: 6px 0 0 0; font-size: 0.65rem; color: #6b7280;">Confianza OCR: ${res.ocr.confidence.toFixed(1)}%</p>`
            : ''}
          ${idExpiryRow}
          ${expiredWarning}
        </div>
      </div>
    `;
  }

  // ============================================================
  // Crear wallet para un usuario (ya seleccionado desde la fila)
  // ============================================================
  /**
   * Crea wallet para el usuario de la fila clickeada. Sin pedir id — ya
   * tenemos `user` en contexto. Tras crear:
   *   1. Llama POST /for-user.
   *   2. Intenta GET /:id/deposit-address?coin=USDT&network=TRX.
   *   3. Modal final muestra subAccount + dirección USDT TRC20 (con copy)
   *      o mensaje "KYC pendiente" si Binance respondió 409.
   *   4. Refresca la fila en la tabla.
   */
  async openCreateWalletModal(user: UserWithWallet): Promise<void> {
    // Empresas: el flujo Binance Link KYC SaaS público sólo soporta
    // personas físicas (kycType USER). Bloqueamos la creación hasta que
    // se defina el flujo ENTITY con Binance — sino la sub queda creada
    // y muerta porque el KYC va a fallar siempre.
    if (this.isEmpresa(user)) {
      await Swal.fire({
        icon: 'warning',
        title: 'Empresas no soportadas todavía',
        html: `
          <div style="text-align: left; font-size: 0.85rem; color: #374151;">
            <p style="margin: 0 0 8px 0;">
              <strong>${this.displayName(user)}</strong> · #${user.usuarioId}
              <span style="display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: 4px; background: #fef3c7; color: #92400e; font-size: 0.7rem; font-weight: 700;">EMPRESA</span>
            </p>
            <p style="margin: 0; font-size: 0.78rem; color: #6b7280;">
              ${user.email} · RUT ${this.displayRut(user)}
            </p>
            <p style="margin: 12px 0 0 0; padding: 10px; background: #fef3c7; border-radius: 6px; font-size: 0.78rem; color: #92400e;">
              Binance Link KYC SaaS sólo soporta personas físicas (<code>kycType: USER</code>) por ahora.
              El flujo para personas jurídicas (<code>ENTITY</code>) está pendiente de definición con Binance.
            </p>
          </div>
        `,
        confirmButtonText: 'Cerrar',
        confirmButtonColor: '#1e5a3f',
        width: '480px',
      });
      return;
    }

    // Persona: abrimos el modal rediseñado con los dos botones explícitos
    // (Registrar Subcuenta + Crear Wallet TRC20). Toda la lógica de
    // estados, polling y errores tipados vive dentro de
    // `CreateWalletModalComponent` — ver FRONTEND_WALLET_MODAL_BRIEF.md.
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open<
      CreateWalletModalComponent,
      CreateWalletModalData,
      CreateWalletModalResult
    >(CreateWalletModalComponent, {
      data: { user },
      panelClass: 'create-wallet-dialog',
      disableClose: false,
      autoFocus: false,
      maxWidth: isMobile ? '95vw' : '720px',
      minWidth: isMobile ? '90vw' : '640px',
      maxHeight: '92vh',
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result?.refreshNeeded) {
      await this.loadUsers();
    }
  }

  // ============================================================
  // Ver detalle
  // ============================================================
  async openDetail(user: UserWithWallet): Promise<void> {
    if (!user.walletId) return;
    const ref = this.spinner.start();
    try {
      const detail = await this.walletAdminService.getWalletById(user.walletId);
      this.spinner.stop(ref);
      await Swal.fire({
        title: `Wallet #${detail.usuarioId}`,
        html: `
          <div style="text-align: left; font-size: 0.85rem; color: #374151;">
            <p style="margin: 0 0 6px 0;"><strong>ID:</strong> ${detail.id}</p>
            <p style="margin: 0 0 6px 0;"><strong>Usuario ID:</strong> ${detail.usuarioId}</p>
            <p style="margin: 0 0 6px 0;"><strong>Sub-account ID:</strong> ${detail.subAccountId}</p>
            <p style="margin: 0 0 6px 0;"><strong>Sub-account Email:</strong> ${detail.subAccountEmail || 'N/A'}</p>
            <p style="margin: 0 0 6px 0;"><strong>Tag:</strong> ${detail.tag}</p>
            <p style="margin: 0 0 6px 0;"><strong>Wallet Status:</strong> ${detail.walletStatus}</p>
            <p style="margin: 0 0 6px 0;"><strong>KYC Status:</strong> ${detail.kycStatus}</p>
            <p style="margin: 0 0 6px 0;"><strong>Activa:</strong> ${detail.activeFlag === 1 ? 'Sí' : 'No'}</p>
            <p style="margin: 0 0 6px 0;"><strong>Creada por:</strong> ${detail.createdByCognitoId || 'No registrado'}</p>
            <p style="margin: 0 0 6px 0;"><strong>Creada:</strong> ${this.getDateFormatted(detail.createdAt)}</p>
            <p style="margin: 0 0 6px 0;"><strong>Actualizada:</strong> ${this.getDateFormatted(detail.updatedAt)}</p>
            <hr style="margin: 12px 0; border: none; border-top: 1px solid #e5e7eb;">
            <p style="margin: 0 0 4px 0; font-size: 0.72rem; font-weight: 700; color: #1e5a3f; text-transform: uppercase; letter-spacing: 0.4px;">Snapshot último sync</p>
            <p style="margin: 0 0 6px 0;"><strong>Balance BTC:</strong> <span style="font-family: monospace;">${this.formatBtc(detail.lastBalanceBtc)}</span></p>
            <p style="margin: 0 0 6px 0;"><strong>USDT en sub:</strong> <span style="font-family: monospace; ${this.hasPositiveSubUsdt(detail) ? 'color: #15803d; font-weight: 700;' : ''}">${this.formatUsd(detail.lastBalanceUsdt)} USDT</span></p>
            <p style="margin: 0 0 6px 0;"><strong>Último sync:</strong> ${this.getDateFormatted(detail.lastBalanceSyncedAt)}</p>
            ${
              detail.activeFlag === null
                ? `
              <hr style="margin: 12px 0; border: none; border-top: 1px solid #e5e7eb;">
              <p style="margin: 0 0 6px 0;"><strong>Deshabilitada:</strong> ${this.getDateFormatted(detail.disabledAt)}</p>
              <p style="margin: 0;"><strong>Razón:</strong> ${detail.disabledReason || 'Sin razón'}</p>
            `
                : ''
            }
          </div>
        `,
        confirmButtonText: 'Cerrar',
        confirmButtonColor: '#1e5a3f',
        width: '520px',
      });
    } catch (error) {
      this.spinner.stop(ref);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo cargar el detalle de la wallet.',
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  // ============================================================
  // Deshabilitar wallet
  // ============================================================
  async openDisableModal(user: UserWithWallet): Promise<void> {
    if (!user.walletId) return;

    const result = await Swal.fire({
      title: 'Deshabilitar wallet',
      html: `
        <p style="margin: 0 0 12px 0;">¿Estás seguro de deshabilitar la wallet de <strong>${this.displayName(user)}</strong> (#${user.usuarioId})?</p>
        <p style="margin: 0; font-size: 0.85rem; color: #6b7280;">El usuario quedará libre para crear una wallet nueva.</p>
      `,
      input: 'text',
      inputLabel: 'Razón (opcional)',
      inputPlaceholder: 'Ej: Compliance review 2026-05-05',
      inputAttributes: {
        maxlength: '255',
      },
      showCancelButton: true,
      confirmButtonText: 'Sí, deshabilitar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#1e5a3f',
    });

    if (!result.isConfirmed) return;

    const reason = (result.value || '').trim() || undefined;
    const ref = this.spinner.start();
    try {
      await this.walletAdminService.disableWallet(user.walletId, reason);
      this.spinner.stop(ref);
      await Swal.fire({
        icon: 'success',
        title: 'Wallet deshabilitada',
        text: 'La wallet fue deshabilitada correctamente.',
        confirmButtonColor: '#1e5a3f',
      });
      await this.loadUsers();
    } catch (error: any) {
      this.spinner.stop(ref);
      const apiMsg = error?.error?.message;
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: apiMsg || 'No se pudo deshabilitar la wallet.',
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  // ============================================================
  // Ver historial de depositos (live call a Binance)
  // ============================================================
  /**
   * Modal con los ultimos 100 depositos de la sub-cuenta. Live call a
   * Binance — puede tardar varios segundos. Maneja 409 (wallet no
   * operativa) y 404 con mensajes claros, evitando que el operador vea
   * JSON crudo.
   */
  async openDepositsHistory(user: UserWithWallet): Promise<void> {
    if (!user.walletId) return;
    if (
      user.walletStatus === 'CREATING' ||
      user.walletStatus === 'ERROR' ||
      user.walletStatus === 'BLOCKED'
    ) {
      Swal.fire({
        icon: 'warning',
        title: 'Wallet no operativa',
        text: `La wallet esta en estado ${user.walletStatus} y no tiene depositos consultables.`,
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }

    const ref = this.spinner.start();
    try {
      const records: DepositRecord[] = await this.walletAdminService.getDepositHistory(
        user.walletId,
      );
      this.spinner.stop(ref);

      if (records.length === 0) {
        await Swal.fire({
          icon: 'info',
          title: 'Sin depositos',
          text: 'Esta sub-cuenta no tiene depositos registrados en Binance.',
          confirmButtonColor: '#1e5a3f',
        });
        return;
      }

      const rows = records
        .map((r) => {
          const colorByStatus: Record<string, string> = {
            SUCCESS: '#15803d',
            PENDING: '#a16207',
            CREDITED_PENDING: '#c2410c',
            OTHER: '#6b7280',
          };
          const statusColor = colorByStatus[r.statusLabel] || '#6b7280';
          return `
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: 6px; font-size: 0.75rem;">${this.getDateFormatted(r.insertTime)}</td>
              <td style="padding: 6px; font-size: 0.75rem; font-weight: 600;">${r.amount} ${r.coin}</td>
              <td style="padding: 6px; font-size: 0.75rem;">${r.network}</td>
              <td style="padding: 6px; font-size: 0.7rem; color: ${statusColor}; font-weight: 600;">${r.statusLabel}</td>
              <td style="padding: 6px; font-size: 0.65rem; font-family: monospace; color: #6b7280; word-break: break-all;">${r.txId.substring(0, 12)}...</td>
            </tr>
          `;
        })
        .join('');

      await Swal.fire({
        title: `Depositos · ${this.displayName(user)}`,
        html: `
          <p style="margin: 0 0 8px 0; font-size: 0.8rem; color: #6b7280;">
            Sub-account <span style="font-family: monospace;">${user.subAccountId}</span> · ${records.length} depositos
          </p>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: #f3f4f6; text-align: left;">
                <th style="padding: 6px; font-size: 0.7rem;">Fecha</th>
                <th style="padding: 6px; font-size: 0.7rem;">Monto</th>
                <th style="padding: 6px; font-size: 0.7rem;">Red</th>
                <th style="padding: 6px; font-size: 0.7rem;">Estado</th>
                <th style="padding: 6px; font-size: 0.7rem;">Tx ID</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        `,
        confirmButtonText: 'Cerrar',
        confirmButtonColor: '#1e5a3f',
        width: '700px',
      });
    } catch (error: any) {
      this.spinner.stop(ref);
      const status = error?.status;
      const apiMsg = error?.error?.message;
      let title = 'Error';
      let text = 'No se pudo obtener el historial de depositos.';
      if (status === 404) {
        title = 'Wallet no encontrada';
        text = 'La wallet ya no existe.';
      } else if (status === 409) {
        title = 'Wallet no operativa';
        text = apiMsg || 'La wallet no esta lista para tener depositos.';
      } else if (apiMsg) {
        text = apiMsg;
      }
      Swal.fire({
        icon: 'warning',
        title,
        text,
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  // ============================================================
  // Ver deposit address (solo USDT por ahora; default red TRC20)
  // ============================================================
  /**
   * Abre un picker simple de red y luego muestra la address devuelta por el
   * backend. Maneja explicitamente el 409 (KYC pendiente / wallet no
   * operativa) que es el caso esperable mas comun mientras KYC SaaS no este
   * conectado: mostramos un mensaje claro en lugar del JSON crudo.
   */
  async openDepositAddress(user: UserWithWallet): Promise<void> {
    if (!user.walletId) return;
    // Si la wallet ni siquiera esta operativa localmente, no llamamos al
    // backend: ya sabemos que va a devolver 409.
    if (
      user.walletStatus === 'CREATING' ||
      user.walletStatus === 'ERROR' ||
      user.walletStatus === 'BLOCKED'
    ) {
      await Swal.fire({
        icon: 'warning',
        title: 'Wallet no operativa',
        text: `La wallet esta en estado ${user.walletStatus} y no puede recibir depositos.`,
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }

    const networkLabels: Record<DepositNetwork, string> = {
      TRX: 'TRC20 (Tron) — recomendada',
      BSC: 'BEP20 (BNB Smart Chain)',
      ETH: 'ERC20 (Ethereum)',
    };

    const { value: network } = await Swal.fire<DepositNetwork>({
      title: 'Direccion de deposito USDT',
      input: 'select',
      inputOptions: networkLabels,
      inputValue: 'TRX',
      inputPlaceholder: 'Selecciona una red',
      showCancelButton: true,
      confirmButtonText: 'Ver direccion',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1e5a3f',
      cancelButtonColor: '#dc2626',
    });

    if (!network) return;

    const ref = this.spinner.start();
    try {
      const data = await this.walletAdminService.getDepositAddressForWallet(
        user.walletId,
        'USDT',
        network as DepositNetwork,
      );
      this.spinner.stop(ref);

      const tagLine =
        data.tag !== null && data.tag !== undefined
          ? `<p style="margin: 8px 0 0 0; font-size: 0.85rem;"><strong>Tag/Memo:</strong> <span class="font-mono">${data.tag}</span></p>`
          : '';

      const result = await Swal.fire({
        title: `Direccion ${data.coin} ${data.network}`,
        html: `
          <div style="text-align: left; font-size: 0.85rem; color: #374151;">
            <p style="margin: 0 0 6px 0;"><strong>Usuario:</strong> ${this.displayName(user)} (#${user.usuarioId})</p>
            <p style="margin: 0 0 12px 0;"><strong>Sub-account:</strong> <span class="font-mono">${data.subAccountId}</span></p>
            <div style="background: #f3f4f6; border-radius: 8px; padding: 12px; word-break: break-all;">
              <span class="font-mono" style="font-size: 0.8rem; color: #1e5a3f; font-weight: 600;">${data.address}</span>
            </div>
            ${tagLine}
            <p style="margin: 12px 0 0 0; font-size: 0.75rem; color: #b91c1c;">
              ⚠ Solo enviar ${data.coin} en red ${data.network}. Otra red = perdida de fondos.
            </p>
          </div>
        `,
        confirmButtonText: 'Copiar direccion',
        showCancelButton: true,
        cancelButtonText: 'Cerrar',
        confirmButtonColor: '#1e5a3f',
        cancelButtonColor: '#6b7280',
        width: '560px',
      });

      if (result.isConfirmed) {
        await navigator.clipboard.writeText(data.address);
        Swal.fire({
          icon: 'success',
          title: 'Copiado',
          text: 'Direccion copiada al portapapeles.',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (error: any) {
      this.spinner.stop(ref);
      const status = error?.status;
      const apiMsg = error?.error?.message;
      const kycStatus = error?.error?.kycStatus;
      const walletStatus = error?.error?.walletStatus;

      let title = 'Error';
      let text = 'No se pudo obtener la direccion de deposito.';

      if (status === 409 && kycStatus) {
        title = 'KYC pendiente';
        text = `La wallet no puede recibir depositos hasta completar la verificacion KYC (estado: ${kycStatus}).`;
      } else if (status === 409 && walletStatus) {
        title = 'Wallet no operativa';
        text = `La wallet esta en estado ${walletStatus}.`;
      } else if (status === 404) {
        title = 'Wallet no encontrada';
        text = 'La wallet ya no existe.';
      } else if (apiMsg) {
        text = apiMsg;
      }

      Swal.fire({
        icon: 'warning',
        title,
        text,
        confirmButtonColor: '#1e5a3f',
      });
    }
  }
}
